// Mobile app welcome screen — the first screen the field team sees after
// installing the app, with a live phone preview beside the form.
//
// The two legal links have no boolean on the wire: an empty string IS the off
// state, so the checkboxes are a UI affordance over link-emptiness. Unticking
// clears the URL; ticking without a valid URL blocks save.
//
// Image uploads on SAVE through its own reserve (welcome-screen-media), sharing
// the one resolver with the branding card.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import * as yup from 'yup';
import SettingsCard, { SettingsRow } from './SettingsCard';
import WelcomeScreenPreview from './WelcomeScreenPreview';
import useCompanyMediaUrl from './useCompanyMediaUrl';
import { Button, Checkbox, Field, Icon, Input, Textarea } from '@/components/common';
import {
  nullIfEmpty,
  useUpdateWelcomeScreenMutation,
  type WelcomeScreen,
} from '@/api/endpoints/companySettingsApi';
import { useWelcomeScreenMediaMutation } from '@/api/endpoints/mediaApi';
import { uploadToPresignedUrl } from '@/utils/uploadFile';
import type { ApiErrorShape } from '@/api/types';

interface WelcomeScreenCardProps {
  welcome: WelcomeScreen;
}

const TITLE_MAX = 40;
const DESCRIPTION_MAX = 140;

// JPG here, not SVG — a photo, unlike the brand logo. Different spec, so the
// accept list is deliberately not shared with CustomBrandingCard.
const ACCEPT = 'image/png,image/jpeg';
const MIN_EDGE = 512;

const urlSchema = yup.string().url();

const dimensionsOk = (file: File): Promise<boolean> =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img.naturalWidth >= MIN_EDGE && img.naturalHeight >= MIN_EDGE);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(false);
    };
    img.src = url;
  });

const WelcomeScreenCard = ({ welcome }: WelcomeScreenCardProps) => {
  const { t } = useTranslation();
  const [updateWelcome, { isLoading: saving }] = useUpdateWelcomeScreenMutation();
  const [reserveMedia] = useWelcomeScreenMediaMutation();
  const fileRef = useRef<HTMLInputElement>(null);

  const seed = useMemo<WelcomeScreen>(() => ({ ...welcome }), [welcome]);
  const [draft, setDraft] = useState<WelcomeScreen>(seed);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [editing, setEditing] = useState(false);
  const [uploading, setUploading] = useState(false);

  // The wire has no boolean for these — an empty link IS the off state. But
  // "ticked, URL not typed yet" has to be expressible while editing, so the
  // toggles live in local state and collapse back to '' on save.
  const [termsOn, setTermsOn] = useState(seed.termsConditionsLink !== '');
  const [privacyOn, setPrivacyOn] = useState(seed.privacyPolicyLink !== '');

  useEffect(() => {
    setDraft(seed);
    setPendingFile(null);
    setTermsOn(seed.termsConditionsLink !== '');
    setPrivacyOn(seed.privacyPolicyLink !== '');
  }, [seed]);

  const patch = (changes: Partial<WelcomeScreen>) => setDraft((d) => ({ ...d, ...changes }));

  const readUrl = useCompanyMediaUrl(seed.media);
  const editUrl = useCompanyMediaUrl(draft.media, pendingFile);

  // A ticked box must end up with a real URL — an empty one would silently
  // save as "off", which is not what the tick says.
  const linkInvalid = (v: string) => !urlSchema.isValidSync(v.trim());
  const termsInvalid = termsOn && linkInvalid(draft.termsConditionsLink);
  const privacyInvalid = privacyOn && linkInvalid(draft.privacyPolicyLink);

  // What a save would actually write, so `dirty` compares outcomes rather than
  // draft text: untick a link and the value is '' regardless of what was typed.
  const termsOut = termsOn ? draft.termsConditionsLink.trim() : '';
  const privacyOut = privacyOn ? draft.privacyPolicyLink.trim() : '';

  const titleMissing = !draft.title.trim();
  const dirty =
    draft.media !== seed.media ||
    draft.title !== seed.title ||
    draft.description !== seed.description ||
    termsOut !== seed.termsConditionsLink ||
    privacyOut !== seed.privacyPolicyLink ||
    pendingFile !== null;
  const canSave = dirty && !titleMissing && !termsInvalid && !privacyInvalid;

  const pickFile = async (file: File | undefined) => {
    if (!file) return;
    if (!ACCEPT.split(',').includes(file.type)) {
      toast.error(t('company_settings_image_type_error'));
      return;
    }
    if (!(await dimensionsOk(file))) {
      toast.error(t('company_settings_image_dimensions_error'));
      return;
    }
    setPendingFile(file);
  };

  const cancel = () => {
    setDraft(seed);
    setPendingFile(null);
    setTermsOn(seed.termsConditionsLink !== '');
    setPrivacyOn(seed.privacyPolicyLink !== '');
    setEditing(false);
  };

  const save = async () => {
    let media = draft.media;
    try {
      if (pendingFile) {
        setUploading(true);
        const ext = pendingFile.name.split('.').pop() || 'jpg';
        const { uploadUrl, mediaId } = await reserveMedia({
          mediaType: 'image',
          imageName: `${Date.now()}.${ext}`,
        }).unwrap();
        await uploadToPresignedUrl(uploadUrl, pendingFile);
        media = mediaId;
      }
    } catch (err) {
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
      return;
    } finally {
      setUploading(false);
    }

    try {
      await updateWelcome({
        media: nullIfEmpty(media),
        title: nullIfEmpty(draft.title),
        description: nullIfEmpty(draft.description),
        // An unticked link is "not entered", so it writes null like everything
        // else — the off state is emptiness either way.
        termsConditionsLink: nullIfEmpty(termsOut),
        privacyPolicyLink: nullIfEmpty(privacyOut),
      }).unwrap();
      setEditing(false);
      setPendingFile(null);
      toast.success(t('company_settings_welcome_saved'));
    } catch (err) {
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
    }
  };

  const legalCard = (
    on: boolean,
    labelKey: string,
    value: string,
    invalid: boolean,
    onToggle: (next: boolean) => void,
    onChange: (next: string) => void,
  ) => (
    <div className={`company-settings__legal-card${on ? ' is-on' : ''}`}>
      {/* The Input is a SIBLING of the Checkbox, never nested: Checkbox renders
          as a <label>, so a nested input would toggle it on every click. */}
      <Checkbox checked={on} onChange={(e) => onToggle(e.target.checked)} label={t(labelKey)} />
      {on && (
        <Input
          dir='ltr'
          value={value}
          error={invalid}
          placeholder='https://example.com'
          aria-label={t(labelKey)}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );

  return (
    <SettingsCard
      title={t('company_settings_welcome_title')}
      subtitle={t('company_settings_welcome_subtitle')}
      editing={editing}
      dirty={dirty}
      saving={saving || uploading}
      canSave={canSave}
      onEdit={() => setEditing(true)}
      onCancel={cancel}
      onSave={() => void save()}
    >
      <div className='company-settings__welcome-body'>
        {editing ? (
          <div className='company-settings__form company-settings__form--flush'>
            <div className='company-settings__row'>
              <p className='company-settings__label'>
                {t('company_settings_image')}
                <span className='company-settings__label-spec'>
                  {t('company_settings_image_spec')}
                </span>
              </p>
              <div className='company-settings__logo-edit'>
                <span className='company-settings__logo-tile company-settings__logo-tile--edit company-settings__logo-tile--round'>
                  {editUrl ? (
                    <img src={editUrl} alt='' className='company-settings__logo-img' />
                  ) : (
                    <Icon name='image' size={22} />
                  )}
                </span>
                <span className='company-settings__logo-meta'>
                  {pendingFile && (
                    <span className='company-settings__logo-name'>{pendingFile.name}</span>
                  )}
                  <span className='company-settings__logo-actions'>
                    <Button
                      variant='secondary'
                      size='sm'
                      iconStart='upload'
                      onClick={() => fileRef.current?.click()}
                    >
                      {t('company_settings_logo_replace')}
                    </Button>
                    {(draft.media || pendingFile) && (
                      <Button
                        variant='secondary'
                        size='sm'
                        iconStart='trash-2'
                        onClick={() => {
                          setPendingFile(null);
                          patch({ media: '' });
                        }}
                      >
                        {t('company_settings_logo_remove')}
                      </Button>
                    )}
                  </span>
                </span>
              </div>
              <p className='company-settings__hint'>{t('company_settings_image_hint')}</p>
              <input
                ref={fileRef}
                type='file'
                accept={ACCEPT}
                className='company-settings__file'
                onChange={(e) => {
                  void pickFile(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
            </div>

            <Field
              label={t('company_settings_welcome_headline')}
              labelTag={`${draft.title.length}/${TITLE_MAX}`}
              error={titleMissing ? t('company_settings_welcome_title_required') : undefined}
            >
              <Input
                value={draft.title}
                error={titleMissing}
                maxLength={TITLE_MAX}
                onChange={(e) => patch({ title: e.target.value })}
              />
            </Field>

            <Field
              label={t('company_settings_welcome_description')}
              labelTag={`${draft.description.length}/${DESCRIPTION_MAX}`}
            >
              <Textarea
                rows={2}
                value={draft.description}
                maxLength={DESCRIPTION_MAX}
                onChange={(e) => patch({ description: e.target.value })}
              />
            </Field>

            <div className='company-settings__row'>
              <p className='company-settings__label'>
                {t('company_settings_legal_links')}
                <span className='company-settings__label-spec'>
                  {t('company_settings_legal_links_hint')}
                </span>
              </p>
              <div className='company-settings__legal'>
                {legalCard(
                  termsOn,
                  'company_settings_legal_terms',
                  draft.termsConditionsLink,
                  termsInvalid,
                  setTermsOn,
                  (next) => patch({ termsConditionsLink: next }),
                )}
                {legalCard(
                  privacyOn,
                  'company_settings_legal_privacy',
                  draft.privacyPolicyLink,
                  privacyInvalid,
                  setPrivacyOn,
                  (next) => patch({ privacyPolicyLink: next }),
                )}
              </div>
              {(termsInvalid || privacyInvalid) && (
                <p className='company-settings__error'>{t('company_settings_link_invalid')}</p>
              )}
            </div>
          </div>
        ) : (
          <div className='company-settings__rows company-settings__rows--flush'>
            <div className='company-settings__row'>
              <p className='company-settings__label'>{t('company_settings_image')}</p>
              <span className='company-settings__logo-tile company-settings__logo-tile--round'>
                {readUrl ? (
                  <img src={readUrl} alt='' className='company-settings__logo-img' />
                ) : (
                  <Icon name='image' size={22} />
                )}
              </span>
            </div>
            <SettingsRow label={t('company_settings_welcome_headline')} value={seed.title} />
            <SettingsRow
              label={t('company_settings_welcome_description')}
              value={seed.description}
            />
            <SettingsRow
              label={t('company_settings_legal_terms_short')}
              value={seed.termsConditionsLink || t('company_settings_link_off')}
              ltr={seed.termsConditionsLink !== ''}
            />
            <SettingsRow
              label={t('company_settings_legal_privacy_short')}
              value={seed.privacyPolicyLink || t('company_settings_link_off')}
              ltr={seed.privacyPolicyLink !== ''}
            />
          </div>
        )}

        <WelcomeScreenPreview
          imageUrl={editing ? editUrl : readUrl}
          imageName={pendingFile?.name}
          title={editing ? draft.title : seed.title}
          description={editing ? draft.description : seed.description}
          termsLink={editing ? termsOut : seed.termsConditionsLink}
          privacyLink={editing ? privacyOut : seed.privacyPolicyLink}
        />
      </div>
    </SettingsCard>
  );
};

export default WelcomeScreenCard;
